// Angular imports
import {
    Component,
    Input,
    OnInit,
    ViewChildren,
    QueryList,
    Output,
    EventEmitter,
    ViewChild,
    AfterViewInit
} from '@angular/core';

// 3rd party imports
import { DxComponent, DxDropDownBoxComponent } from 'devextreme-angular';
import { dxElement } from 'devextreme/core/element';
import { SplitComponent } from 'angular-split';

// Service imports
import { HelperService } from '../../_services/shared-services/helper.service';
import { ScreenService } from '../../_services/shared-services/screen.service';
import { DynamicDashboardService } from '../_services/dynamic-dashboard.service';

// Directive imports
import { DynamicDashboardTemplateDirective } from '../_directives/dynamic-dashboard-template.directive';

// Model imports
import { DynamicDashboardItem } from '../_model/dynamic-dashboard-item';
import { DynamicDashboardWidget } from '../_model/dynamic-dashboard-widget';
import { DashboardLayout } from '../_model/dynamic-dashboard-section';
import { DashboardViewMode } from '../_model/dynamic-dashboard-entry';

@Component({
    selector: 'app-dynamic-dashboard',
    templateUrl: './dynamic-dashboard.component.html',
    styleUrls: ['./dynamic-dashboard.component.scss']
})
export class DynamicDashboardComponent implements OnInit, AfterViewInit {
    @ViewChildren(DynamicDashboardTemplateDirective) dashboardTemplates: QueryList<DynamicDashboardTemplateDirective>;
    @ViewChildren('dropDownBox') dropDownBoxes: QueryList<DxDropDownBoxComponent>;
    @ViewChild('mainView', { static: false }) mainView: DxComponent;
    @ViewChild('rootSplit', { static: false }) rootSplit: SplitComponent;
    @ViewChild('topSplit', { static: false }) topSplit: SplitComponent;
    @ViewChild('bottomSplit', { static: false }) bottomSplit: SplitComponent;

    @Input() showTitle = true;
    @Input() autoFitView: boolean;
    @Input() editMode: boolean;
    @Input() viewMode: DashboardViewMode = DashboardViewMode.Carousel;
    @Input() direction: 'horizontal' | 'vertical' = 'horizontal';
    @Input() layout: DashboardLayout = DashboardLayout.OneByOne;
    @Input() dashboardItems: DynamicDashboardItem[] = [];
    @Output() onWidgetChange: EventEmitter<boolean> = new EventEmitter();
    @Output() onResize: EventEmitter<boolean> = new EventEmitter();
    @Output() onFullScreen: EventEmitter<boolean> = new EventEmitter();

    screenWidth = -1;
    screenHeight = -1;
    boxWidth = -1;
    boxHeight = -1;
    dashboardWidgets: DynamicDashboardWidget[];
    groupedDashboardWidgets: any[];
    hasGroupedWidgets: boolean;

    private _timerId: any;
    private _isComputing = false;

    constructor(
        private readonly screenService: ScreenService,
        private readonly helperService: HelperService,
        private readonly dashboardService: DynamicDashboardService
    ) {
        this.dashboardService.getDashboardWidgets().subscribe((dashboardWidgets) => {
            this.dashboardWidgets = dashboardWidgets;
            this.groupedDashboardWidgets = this.dashboardService.getGroupedDashboardWidgets(dashboardWidgets);
            this.hasGroupedWidgets = this.groupedDashboardWidgets.length > 0;
        });
    }

    ngOnInit(): void {
        this.helperService.subjectFullScreenWidget.subscribe((widgetId: string) => {
            if (!!widgetId) {
                const tile = this.dashboardItems.find((di) => di.widgetId === widgetId);
                if (!!tile) {
                    this.toggleFullScreen(tile);
                }
            }
        });
        this.helperService.subjectSideNavSizeChanged.subscribe(() => {
            setTimeout(() => this.computeTileSize(), 250);
        });
        this.screenService.changed.subscribe(() => {
            setTimeout(() => this.computeTileSize(), 250);
        });
    }

    ngAfterViewInit(): void {
        setTimeout(() => this.computeTileSize(), 250);
    }

    onTileReady(tile: DynamicDashboardItem): void {
        setTimeout(() => tile.updateTitle(), 250);
    }

    onValidatorInit(e: any, tile: DynamicDashboardItem): void {
        tile.widgetValidator = e.component;
    }

    toggleFullScreen(tile: DynamicDashboardItem): void {
        tile.isFullScreen = !tile.isFullScreen;
        const fullscreen = document.getElementById(`fullscreen-container`);
        const content = document.getElementById(`tile-content-${tile.id || ''}-${tile.widgetId || ''}`);
        const container = document.getElementById(`tile-container-${tile.id || ''}-${tile.widgetId || ''}`);
        if (tile.isFullScreen) {
            fullscreen.appendChild(content);
            fullscreen.classList.remove(`cc-hidden-box`);
        } else {
            container.appendChild(content);
            fullscreen.classList.add(`cc-hidden-box`);
        }
        setTimeout(() => {
            const elm = document.getElementById(`widget-${tile.id || ''}-${tile.widgetId || ''}`);
            if (!!elm) {
                tile.refresh(elm.clientWidth, elm.clientHeight);
            }
            this.onFullScreen.emit(tile.isFullScreen);
        }, 250);
    }

    onDashboardWidgetChange(e: any, tile: DynamicDashboardItem): void {
        const selected: DynamicDashboardWidget = e.selectedItem || e.itemData;
        if (!!selected && !!selected.id && !!tile && selected.id !== tile.widgetId) {
            tile.originalWidgetId = !!tile.originalWidgetId ? tile.originalWidgetId : tile.widgetId;
            tile.title = selected.name;
            tile.helpLink = selected.helpLink;
            tile.isConfigVisible = false;
            tile.widgetId = null;
            setTimeout(() => {
                tile.widgetId = selected.id;
                tile.updateTitle();
                tile.validate();
            }, 0);

            if (!!this.dropDownBoxes) {
                const dropDownBox: DxDropDownBoxComponent = this.dropDownBoxes.find(
                    (ddb) => ddb.elementAttr.tile.widgetId === tile.widgetId
                );
                if (!!dropDownBox) {
                    dropDownBox.instance.close();
                }
            }
        }
    }

    onRevertWidgetChange(tile: DynamicDashboardItem): void {
        const orignal: DynamicDashboardWidget = this.dashboardWidgets.find((w) => w.id === tile.originalWidgetId);
        tile.title = orignal.name;
        tile.helpLink = orignal.helpLink;
        tile.widgetId = null;
        tile.originalWidgetId = null;
        setTimeout(() => {
            tile.widgetId = orignal.id;
            tile.updateTitle();
        }, 0);
    }

    updateWidthRatio(
        event: { gutterNum: number; sizes: number[] },
        leftIndex: number,
        rightIndex: number,
        split: SplitComponent
    ): void {
        if (this.viewMode === DashboardViewMode.Scroll) {
            const effectiveSizes = event.sizes.slice();
            let ratioDiff = 0;
            this.dashboardItems.forEach((di, idx) => {
                const newRatio = event.sizes[idx] - ratioDiff;
                effectiveSizes[idx] = Math.max(newRatio, di.minWidthRatio);
                ratioDiff = Math.max(0, di.minWidthRatio - newRatio);
                di.widthRatio = effectiveSizes[idx];
            });
            split.setVisibleAreaSizes(effectiveSizes);
        } else {
            let [leftRatio, rightRatio] = event.sizes;
            const leftItem = this.dashboardItems[leftIndex];
            const rightItem = this.dashboardItems[rightIndex];
            if (split.direction === 'horizontal') {
                if (leftRatio < rightRatio && leftItem.minWidthRatio > leftRatio) {
                    leftRatio = leftItem.minWidthRatio;
                    rightRatio = 100 - leftRatio;
                    split.setVisibleAreaSizes([leftRatio, rightRatio]);
                } else if (leftRatio > rightRatio && rightItem.minWidthRatio > rightRatio) {
                    rightRatio = rightItem.minWidthRatio;
                    leftRatio = 100 - rightRatio;
                    split.setVisibleAreaSizes([leftRatio, rightRatio]);
                }
                this.dashboardItems[leftIndex].widthRatio = leftRatio;
                this.dashboardItems[rightIndex].widthRatio = rightRatio;
            } else {
                if (leftRatio < rightRatio && leftItem.minHeightRatio > leftRatio) {
                    leftRatio = leftItem.minHeightRatio;
                    rightRatio = 100 - leftRatio;
                    split.setVisibleAreaSizes([leftRatio, rightRatio]);
                } else if (leftRatio > rightRatio && rightItem.minHeightRatio > rightRatio) {
                    rightRatio = rightItem.minHeightRatio;
                    leftRatio = 100 - rightRatio;
                    split.setVisibleAreaSizes([leftRatio, rightRatio]);
                }
                this.dashboardItems[leftIndex].heightRatio = leftRatio;
                this.dashboardItems[rightIndex].heightRatio = rightRatio;
            }
        }
        this.onResize.emit(true);
        setTimeout(() => this.computeTileSize(), 250);
    }

    updateHeightRatio(event: { gutterNum: number; sizes: number[] }): void {
        let [topRatio, bottomRatio] = event.sizes;
        const topMinRatio = Math.max(this.dashboardItems[0].minHeightRatio, this.dashboardItems[1].minHeightRatio);
        const bottomMinRatio = Math.max(this.dashboardItems[2].minHeightRatio, this.dashboardItems[3].minHeightRatio);
        if (topRatio < bottomRatio && topMinRatio > topRatio) {
            topRatio = topMinRatio;
            bottomRatio = 100 - topRatio;
            this.rootSplit.setVisibleAreaSizes([topRatio, bottomRatio]);
        } else if (topRatio > bottomRatio && bottomMinRatio > bottomRatio) {
            bottomRatio = bottomMinRatio;
            topRatio = 100 - bottomRatio;
            this.rootSplit.setVisibleAreaSizes([topRatio, bottomRatio]);
        }

        this.dashboardItems[0].heightRatio = topRatio;
        this.dashboardItems[1].heightRatio = topRatio;
        this.dashboardItems[2].heightRatio = bottomRatio;
        this.dashboardItems[3].heightRatio = bottomRatio;
        this.onResize.emit(true);
        setTimeout(() => this.computeTileSize(), 250);
    }

    computeTileSize(): void {
        clearTimeout(this._timerId);
        this._timerId = setTimeout(() => {
            if (!!this.mainView && this.mainView.instance && !this._isComputing) {
                this._isComputing = true;
                // tslint:disable-next-line:deprecation
                const el: dxElement = this.mainView.instance.element();
                this.boxWidth = el.clientWidth;
                this.boxHeight = el.clientHeight;
                this.screenWidth = window.innerWidth;
                this.screenHeight = window.innerHeight;
                this.dashboardItems.forEach((tile, idx) =>
                    setTimeout(() => {
                        if (this.autoFitView) {
                            tile.calcMinWidthRatio = tile.minWidthRatio;
                            tile.calcMinHeightRatio = tile.minHeightRatio;
                        } else {
                            tile.calcMinWidthRatio = 100 * ((this.boxWidth - tile.minWidth || 0) / this.boxWidth);
                            tile.calcMinHeightRatio = 100 * ((this.boxHeight - tile.minHeight || 0) / this.boxHeight);
                        }
                        const elm = document.getElementById(`widget-${tile.id || ''}-${tile.widgetId || ''}`);
                        if (!!elm) {
                            tile.refresh(elm.clientWidth, elm.clientHeight);
                        }
                        this._isComputing = idx !== this.dashboardItems.length - 1;
                    }, 100)
                );
            }
        }, 250);
    }
}
